import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import _ from 'lodash';

import * as graphql from 'generated/graphql';
import { EvaluationFormFragment } from 'generated/graphql';
import {
	Checkbox,
	Input,
	Label,
	Paragraph,
	Radio,
	Select,
	Separator,
	Spinner,
	Subheading,
} from '@treams/treams-ui';
import moment from 'moment';
import useViewerRoles from '../packages/kakapo-ui-application/hooks/useUserRoles';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';
import { useMomentJs } from 'utility';
import { SelectConversationType } from './create-conversation';

export function ConversationTemplateSelect(props: {
	titlePrefix?: string;
	value?: string;
	onChange: (evaluationFormID: string | undefined) => void;
}) {
	const { t } = useTranslation();

	const { data, loading } = graphql.useEvaluationFormsQuery({
		variables: {
			isArchived: false,
			first: 1000,
		},
	});

	const options: EvaluationFormFragment[] | undefined = data?.evaluationForms.edges.map((edge) => {
		return edge.node;
	});

	return (
		<>
			<Subheading color="antracite" mb={2}>
				{props.titlePrefix ? `${props.titlePrefix} ` : ''}
				{t('evaluations.create.modal.formLabel')}
			</Subheading>

			{loading ? (
				<Flex justifyContent="center" mb={3}>
					<Spinner />
				</Flex>
			) : (
				options && <SelectConversationType forms={options} onChange={props.onChange} />
			)}
		</>
	);
}

export function ConversationPartnerSelect(props: {
	moment?: boolean;
	titlePrefix?: string;
	value: {
		subjectType: 'me' | 'partner';
		userID?: string;
	};
	onChange: (value: { subjectType: 'me' | 'partner'; userID?: string }) => void;
}) {
	const { t } = useTranslation();

	const { data: managersData } = graphql.useViewerWithManagersQuery();

	const viewer = useViewer();
	const roles = useViewerRoles();
	const query = graphql.useManagerInboxQuery({
		variables: {
			recurse: false,
		},
		skip: roles.isWorkspaceSuperuser,
	});

	const { data: employeesData } = graphql.useOrganizationEmployeesQuery({
		variables: {
			status: graphql.UserStatus.Active,
			first: 10000,
		},
	});

	const managees = query.data?.viewer.managees ?? [];
	const manageesMap = _.groupBy(managees, 'id');
	const managers = managersData?.viewer.managers ?? [];
	const managersMap = _.groupBy(managers, 'source.id');

	const filteredEmployees = employeesData?.users.edges.filter(({ node }) => {
		if (node.id === viewer?.id) {
			return false;
		}

		if (props.value.subjectType === 'me') {
			return managersMap[node.id] === undefined;
		}
		return manageesMap[node.id] === undefined;
	});

	const suggestions = () => {
		const all = {
			title: t('conversations.create.modal.partner.companyLabel'),
			options: (filteredEmployees ?? []).map(({ node }) => ({
				title: `${node.firstName} ${node.lastName}`,
				value: node.id,
			})),
		};

		if (
			(props.value.subjectType === 'partner' && managees.length === 0) ||
			(props.value.subjectType === 'me' && managers.length === 0)
		) {
			return [all];
		}

		return [
			props.value.subjectType === 'partner'
				? {
						title: t('conversations.create.modal.partner.teamLabel'),
						options: _.sortBy(managees, (managee) => managee.firstName).map((user) => ({
							title: `${user.firstName} ${user.lastName}`,
							value: user.id,
						})),
				  }
				: {
						title: t('conversations.create.modal.partner.managerLabel'),
						options: _.sortBy(managers, (manager) => manager.source.firstName).map((role) => ({
							title: `${role.source.firstName} ${role.source.lastName}`,
							value: role.source.id,
						})),
				  },
			all,
		];
	};

	if (roles.loading) {
		return (
			<Flex justifyContent="center">
				<Spinner />
			</Flex>
		);
	}

	return (
		<>
			<Subheading color="antracite" mb={2}>
				{props.titlePrefix ? `${props.titlePrefix} ` : ''}
				{t('evaluations.create.modal.partnerLabel')}
			</Subheading>

			{roles.isManager && !props.moment && (
				<Box mb={3}>
					<Paragraph mb={1}>{t('conversations.create.partner.subjectType')}</Paragraph>
					<Flex>
						<Flex
							mr={3}
							alignItems="center"
							onClick={() => props.onChange({ ...props.value, subjectType: 'partner' })}
							style={{ cursor: 'pointer' }}
						>
							<Radio mr={1} checked={props.value.subjectType === 'partner'} onChange={() => {}} />
							<Label>{t('conversations.create.partner.options.partner')}</Label>
						</Flex>
						<Flex
							mr={3}
							alignItems="center"
							onClick={() => props.onChange({ ...props.value, subjectType: 'me' })}
							style={{ cursor: 'pointer' }}
						>
							<Radio mr={1} checked={props.value.subjectType === 'me'} onChange={() => {}} />
							<Label>{t('conversations.create.partner.options.me')}</Label>
						</Flex>
					</Flex>
				</Box>
			)}

			<Paragraph mb={1}>
				{props.value.subjectType === 'me'
					? t('conversations.create.partner.select.manager.label')
					: t('conversations.create.partner.select.managee.label')}
			</Paragraph>
			<Flex>
				<Select
					placeholder={t(`conversations.create.modal.${props.value.subjectType}.empty`)}
					selectedValue={props.value.userID}
					onChange={(userID) => props.onChange({ ...props.value, userID })}
					options={suggestions()}
				/>
			</Flex>
			{props.value.userID && <Separator my={3} />}
		</>
	);
}

function ConversationMeetingPlanner(props: {
	checkbox?: boolean;
	selected: boolean;
	onSelect: (selected: boolean) => void;
	startTime?: Date;
	duration: number;
	location: string;
	onChange: (startTime: Date, duration: number, location: string) => void;
}) {
	const { nearestMinutes } = useMomentJs();
	const { t } = useTranslation('', { keyPrefix: 'evaluationSubmissions.create.meetingPlanner' });

	/* Value definitions */
	const endRef = useRef<null | HTMLDivElement>(null);

	const time = nearestMinutes(15, moment(props.startTime));
	const timeLabel = time.format('HH:mm');

	const currentDay = moment(props.startTime).startOf('day');

	const options = useMemo(
		() =>
			_.range(8, 19).flatMap((hour) => {
				const time = currentDay.set({ hour: hour });

				return [0, 15, 30, 45].map((minutes) => {
					time.set({ minutes: minutes });
					return {
						value: time.toDate().toString(),
						title: time.format('HH:mm').toString(),
					};
				});
			}),
		[currentDay],
	);

	const minutesLabel = t('duration.minutes');
	const hourLabel = t('duration.hour');
	const meetingDurationOptions = [
		{
			title: `15 ${minutesLabel}`,
			value: '15',
		},
		{
			title: `30 ${minutesLabel}`,
			value: '30',
		},
		{
			title: `45 ${minutesLabel}`,
			value: '45',
		},
		{
			title: `1 ${hourLabel}`,
			value: '60',
		},
		{
			title: `1 ${hourLabel} & 15 ${minutesLabel}`,
			value: '75',
		},
		{
			title: `1 ${hourLabel} & 30 ${minutesLabel}`,
			value: '90',
		},
	].reverse();

	const scrollToBottom = () => {
		endRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const handleChange = (duration: number, location: string, value?: string) => {
		if (value) {
			props.onChange(new Date(value), duration, location);
		}
	};

	if (props.selected) {
		scrollToBottom();
	}

	return (
		<>
			<Flex>
				{props.checkbox !== false && (
					<Box flexGrow={0} flexShrink={0} mr={2}>
						<Checkbox checked={props.selected} onClick={() => props.onSelect(!props.selected)} />
					</Box>
				)}
				<Box flexGrow={1} flexShrink={1}>
					{props.checkbox !== false && (
						<Paragraph onClick={() => props.onSelect(true)} mb={3}>
							{t('description')}
						</Paragraph>
					)}
					{props.selected && (
						<>
							<Separator mb={3} />
							<Flex mb={3}>
								<Box mr={2}>
									<Label mb={1}>{t('startTime.label')}:</Label>
									<Select
										selectedOption={{
											value: props.startTime?.toString(),
											title: timeLabel,
										}}
										onChange={(value) => handleChange(props.duration, props.location, value)}
										options={options}
									/>
								</Box>
								<Box ml={2}>
									<Label mb={1}>{t('duration.label')}:</Label>
									<Select
										selectedValue={String(props.duration)}
										onChange={(value) =>
											handleChange(Number(value), props.location, props.startTime?.toString())
										}
										options={meetingDurationOptions}
									/>
								</Box>
							</Flex>
							<Flex flexDirection="column">
								<Label mb={1}>{t('location.label')}</Label>
								<Input
									value={props.location}
									placeholder={t('location.placeholder')}
									onChange={(value) =>
										handleChange(props.duration, value, props.startTime?.toString())
									}
								/>
							</Flex>
						</>
					)}
				</Box>
			</Flex>
			<div ref={endRef} />
		</>
	);
}

export function ConversationDateSelect(props: {
	invite?: boolean;
	value?: { deadline?: Date; meetingDuration?: number; location: string };
	onChange: (value: {
		deadline: Date | undefined;
		meetingDuration?: number;
		location: string;
	}) => void;
	titlePrefix?: string;
}) {
	const { t } = useTranslation('', { keyPrefix: 'evaluations.create.modal.dueDate' });

	const [deadline, setDeadline] = useState(props.value?.deadline);
	const [meetingDuration, setMeetingDuration] = useState(props.value?.meetingDuration ?? 60);
	const [location, setLocation] = useState(props.value?.location ?? '');
	const [startTime, setStartTime] = useState(
		props.value?.deadline ? props.value.deadline : moment().set({ hour: 9 }).toDate(),
	);

	const [invite, setInvite] = useState(props.invite ?? false);

	const handleMeetingPlanner = (startTime: Date, duration: number, location: string) => {
		setStartTime(startTime);
		setMeetingDuration(duration);
		setDeadline(startTime);
		setLocation(location);
	};

	const handleDatePicker = (deadline: Date | undefined) => {
		if (deadline) {
			setDeadline(deadline);
			setStartTime(deadline);
		}
	};

	useEffect(() => {
		props.onChange({
			deadline: startTime,
			meetingDuration: invite ? meetingDuration : undefined,
			location: location,
		});
	}, [deadline, startTime, meetingDuration, location, invite, props]);

	return (
		<>
			<Subheading>
				{props.titlePrefix ? `${props.titlePrefix} ` : ''}
				{t('label')}
			</Subheading>
			<Paragraph mb={3}>{t('description')}</Paragraph>
			<Label mb={2}>{t('label')}</Label>
			<Flex>
				<Input.DatePicker
					value={props.value?.deadline}
					onChange={(deadline) => handleDatePicker(deadline)}
				/>
			</Flex>

			<Box mt={3}>
				<ConversationMeetingPlanner
					checkbox={typeof props.invite !== 'boolean'}
					selected={invite}
					onSelect={setInvite}
					startTime={startTime}
					duration={meetingDuration}
					location={location}
					onChange={(startTime, duration, location) =>
						handleMeetingPlanner(startTime, duration, location)
					}
				/>
			</Box>
		</>
	);
}
